/**
 * 休日種類の登録・編集ページ (/tenant/holiday/new, /tenant/holiday/:id/edit)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { Button } from "../components/atoms";
import { PaletteColorPicker } from "../components/molecules/PaletteColorPicker";
import {
    fetchDayOffTypes,
    createDayOffType,
    updateDayOffType,
    deleteDayOffType,
    type DayOffTypeResponse,
} from "../api/dayOffTypes";

/** フォームデータ */
interface DayOffTypeFormData {
    code: string;
    name: string;
    short_name: string;
    description: string;
    background_color: string;
    text_color: string;
    is_work_time: number;
    day_count_type: number;
    // 自動作成バッチ専用（autogen_ プレフィックス）
    autogen_count_in_consecutive_workdays: number;
    autogen_count_in_consecutive_days_off: number;
    autogen_count_in_day_off_count: number;
    autogen_count_in_working_hours_monthly: number;
}

const DEFAULT_BG = "";
const DEFAULT_TEXT = "#1f2937";

const IS_WORK_TIME_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: "含める" },
    { value: 2, label: "含めない" },
];

const DAY_COUNT_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: "勤務日数" },
    { value: 2, label: "公休日数" },
    { value: 3, label: "カウントしない" },
];

/** 自動作成バッチ専用フラグの定義 */
const AUTOGEN_FLAGS: { key: keyof DayOffTypeFormData; label: string; description: string }[] = [
    { key: "autogen_count_in_consecutive_workdays",  label: "連勤で勤務日としてカウント",   description: "連勤日数を計算するときに、この休日種別を勤務日扱いする" },
    { key: "autogen_count_in_consecutive_days_off",  label: "連休で休日としてカウント",     description: "連続休日数を計算するときに、この休日種別を休日扱いする" },
    { key: "autogen_count_in_day_off_count",         label: "月間公休日数としてカウント",   description: "月間公休日数の集計に含める" },
    { key: "autogen_count_in_working_hours_monthly", label: "月間勤務時間にカウント",       description: "月間勤務時間の集計に含める" },
];

/** サイドメニュー項目（休日種類は基本情報のみ） */
const SIDE_MENU_ITEMS = [
    { id: "1", label: "1. 基本情報" },
] as const;

const FormField = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-start gap-6">
        <label className="w-28 flex-shrink-0 pt-2 text-sm text-gray-700">{label}</label>
        <div className="flex-1">{children}</div>
    </div>
);

const toFormData = (dt: DayOffTypeResponse): DayOffTypeFormData => ({
    code: dt.code,
    name: dt.name,
    short_name: dt.short_name ?? "",
    description: dt.description ?? "",
    background_color: dt.background_color ?? DEFAULT_BG,
    text_color: dt.text_color ?? DEFAULT_TEXT,
    is_work_time: dt.is_work_time,
    day_count_type: dt.day_count_type,
    autogen_count_in_consecutive_workdays: dt.autogen_count_in_consecutive_workdays,
    autogen_count_in_consecutive_days_off: dt.autogen_count_in_consecutive_days_off,
    autogen_count_in_day_off_count: dt.autogen_count_in_day_off_count,
    autogen_count_in_working_hours_monthly: dt.autogen_count_in_working_hours_monthly,
});

const DayOffTypeFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const typeId = id ? parseInt(id, 10) : null;

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSystem, setIsSystem] = useState(false);
    const [formData, setFormData] = useState<DayOffTypeFormData>({
        code: "",
        name: "",
        short_name: "",
        description: "",
        background_color: DEFAULT_BG,
        text_color: DEFAULT_TEXT,
        is_work_time: 2,
        day_count_type: 1,
        autogen_count_in_consecutive_workdays: 0,
        autogen_count_in_consecutive_days_off: 1,
        autogen_count_in_day_off_count: 1,
        autogen_count_in_working_hours_monthly: 0,
    });

    useEffect(() => {
        if (!isEdit || !typeId) return;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchDayOffTypes();
                const item = data.items.find((dt) => dt.id === typeId);
                if (!item) {
                    setError("休日種類が見つかりません");
                    return;
                }
                setFormData(toFormData(item));
                setIsSystem(item.is_system === 1);
            } catch (err) {
                setError(err instanceof Error ? err.message : "データの取得に失敗しました");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isEdit, typeId]);

    const handleChange = useCallback(
        <K extends keyof DayOffTypeFormData>(field: K, value: DayOffTypeFormData[K]) => {
            setFormData((prev) => ({ ...prev, [field]: value }));
        },
        [],
    );

    const handleCancel = () => navigate("/tenant/holiday");

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                code: formData.code,
                name: formData.name,
                short_name: formData.short_name || null,
                description: formData.description || null,
                background_color: formData.background_color || null,
                text_color: formData.text_color || null,
                is_work_time: formData.is_work_time,
                day_count_type: formData.day_count_type,
                autogen_count_in_consecutive_workdays: formData.autogen_count_in_consecutive_workdays,
                autogen_count_in_consecutive_days_off: formData.autogen_count_in_consecutive_days_off,
                autogen_count_in_day_off_count: formData.autogen_count_in_day_off_count,
                autogen_count_in_working_hours_monthly: formData.autogen_count_in_working_hours_monthly,
            };
            if (isEdit && typeId) {
                await updateDayOffType(typeId, payload);
            } else {
                await createDayOffType(payload);
            }
            navigate("/tenant/holiday");
        } catch (err) {
            setError(err instanceof Error ? err.message : "保存に失敗しました");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!typeId) return;
        if (!window.confirm("この休日種類を削除しますか？")) return;
        setDeleting(true);
        setError(null);
        try {
            await deleteDayOffType(typeId);
            navigate("/tenant/holiday");
        } catch (err) {
            setError(err instanceof Error ? err.message : "削除に失敗しました");
            setDeleting(false);
        }
    };

    const title = isEdit ? "休日種類の編集" : "休日種類の登録";
    const previewLabel = formData.short_name || formData.name || "---";

    if (loading) {
        return (
            <div className="flex flex-1 flex-col overflow-hidden">
                <div className="px-6 py-4 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="h-6 w-1 rounded-full bg-brand-500" />
                        <h1 className="text-xl font-bold text-gray-800">{title}</h1>
                    </div>
                </div>
                <div className="flex flex-1 items-center justify-center">
                    <span className="text-gray-500">読み込み中...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            {/* ヘッダー */}
            <div className="px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="h-6 w-1 rounded-full bg-brand-500" />
                    <h1 className="text-xl font-bold text-gray-800">{title}</h1>
                </div>
                {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
                {isSystem && (
                    <div className="mt-2 text-sm text-yellow-700">
                        システム予約の休日種類です。コードは変更できません（名称・色・自動作成設定は変更可）。削除はできません。
                    </div>
                )}
            </div>

            {/* フォームコンテンツ */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
                <div className="flex h-full gap-4">
                    {/* サイドメニューエリア */}
                    <div className="w-44 flex-shrink-0 rounded-lg bg-white py-4">
                        <nav className="space-y-1">
                            {SIDE_MENU_ITEMS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={clsx(
                                        "w-full px-4 py-2 text-left text-sm",
                                        item.id === "1"
                                            ? "font-medium text-brand-500"
                                            : "text-gray-600 hover:text-gray-900",
                                    )}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* フォームコンテンツエリア */}
                    <div className="flex flex-1 flex-col overflow-hidden rounded-lg bg-white">
                        <div className="flex-1 overflow-auto p-6">
                            <div className="space-y-6">
                                <FormField label="休日コード">
                                    <input
                                        type="text"
                                        value={formData.code}
                                        onChange={(e) => handleChange("code", e.target.value)}
                                        placeholder="例: PAID_LEAVE"
                                        className="form-input-field w-60"
                                        disabled={isSystem}
                                        title={isSystem ? "システム予約の休日種別は code を変更できません" : undefined}
                                    />
                                </FormField>
                                <FormField label="休日名">
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleChange("name", e.target.value)}
                                        placeholder="例: 有休"
                                        className="form-input-field w-60"
                                    />
                                </FormField>
                                <FormField label="略称">
                                    <input
                                        type="text"
                                        value={formData.short_name}
                                        onChange={(e) => handleChange("short_name", e.target.value)}
                                        placeholder="例: 有"
                                        className="form-input-field w-60"
                                    />
                                </FormField>
                                <FormField label="備考">
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => handleChange("description", e.target.value)}
                                        placeholder="備考を入力"
                                        className="form-input-field w-full max-w-md"
                                        disabled={isSystem}
                                    />
                                </FormField>
                                <FormField label="勤務時間に含める">
                                    <select
                                        value={formData.is_work_time}
                                        onChange={(e) => handleChange("is_work_time", Number(e.target.value))}
                                        className="form-input-field w-60"
                                        disabled={isSystem}
                                    >
                                        {IS_WORK_TIME_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </FormField>
                                <FormField label="日数カウント">
                                    <select
                                        value={formData.day_count_type}
                                        onChange={(e) => handleChange("day_count_type", Number(e.target.value))}
                                        className="form-input-field w-60"
                                        disabled={isSystem}
                                    >
                                        {DAY_COUNT_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </FormField>
                                <FormField label="背景色">
                                    <PaletteColorPicker
                                        value={formData.background_color}
                                        onChange={(hex) => handleChange("background_color", hex)}
                                        presetOptions={[{ label: "背景色なし", value: "" }]}
                                    />
                                </FormField>
                                <FormField label="文字色">
                                    <PaletteColorPicker
                                        value={formData.text_color}
                                        onChange={(hex) => handleChange("text_color", hex)}
                                        presetOptions={[
                                            { label: "デフォルト（黒）", value: "#1f2937" },
                                            { label: "白", value: "#ffffff" },
                                        ]}
                                    />
                                </FormField>
                                <FormField label="プレビュー">
                                    <span
                                        className="inline-block rounded px-3 py-1 text-sm font-medium"
                                        style={{
                                            backgroundColor: formData.background_color,
                                            color: formData.text_color,
                                        }}
                                    >
                                        {previewLabel}
                                    </span>
                                </FormField>

                                {/* 自動作成バッチ専用フラグ */}
                                <div className="border-t pt-4">
                                    <h2 className="mb-1 text-sm font-medium text-gray-700">自動作成バッチ設定</h2>
                                    <p className="mb-3 text-xs text-gray-500">
                                        シフト自動作成バッチが制約計算するときに、この休日種別をどう扱うかを指定します。
                                    </p>
                                    <div className="space-y-2">
                                        {AUTOGEN_FLAGS.map((flag) => (
                                            <label key={flag.key} className="flex items-start gap-3 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={formData[flag.key] === 1}
                                                    onChange={(e) => handleChange(flag.key, e.target.checked ? 1 : 0)}
                                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                                                />
                                                <span>
                                                    <span className="font-medium text-gray-800">{flag.label}</span>
                                                    <span className="ml-2 text-xs text-gray-500">{flag.description}</span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* フッターボタン */}
                        <div className="flex items-center justify-between px-6 py-4">
                            <div>
                                {isEdit && !isSystem && (
                                    <Button variant="danger" size="md" onClick={handleDelete} disabled={deleting || saving} className="px-6">
                                        {deleting ? "削除中..." : "削除"}
                                    </Button>
                                )}
                            </div>
                            <div className="flex gap-4">
                                <Button variant="outline" size="md" onClick={handleCancel} disabled={saving || deleting} className="px-8">
                                    キャンセル
                                </Button>
                                <Button variant="primary" size="md" onClick={handleSave} disabled={saving || deleting} className="px-8">
                                    {saving ? "保存中..." : "保存"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DayOffTypeFormPage;
